import { Stack } from '@mui/material';
import { isUndefined } from 'lodash';
import { PropsWithChildren, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import { useFormContext } from '@/components/react-hook-form';
import { AccessParameterKind } from '@/types/schema';
import { PathsOf } from '@/types/subform';

import {
  BeneficiaryFormAccessParameters,
  DistributionType,
  EntityBeneficiaryAccessFrequencies,
} from './EntityBeneficiariesSubform.types';
import {
  PartialAccessParameterForm,
  PartialAccessParameterFormVariant,
} from './PartialAccessParameterForm';

function SubformContainer({ children }: PropsWithChildren) {
  return <Stack>{children}</Stack>;
}

type AccessParameterLimitedFormPaths = PathsOf<BeneficiaryFormAccessParameters>;

interface AccessParameterLimitedFormProps {
  fieldName: 'beneficiaryFormAccessParameters';
  beneficiaryId: string;
}

export function AccessParameterLimitedForm({
  fieldName,
  beneficiaryId,
}: AccessParameterLimitedFormProps) {
  const { control, setValue } =
    useFormContext<BeneficiaryFormAccessParameters>();

  const [checkboxes = {}, hasAmountOrPercentage] = useWatch({
    control,
    name: [
      `${fieldName}.checkboxes` as 'checkboxes' satisfies AccessParameterLimitedFormPaths,
      `${fieldName}._hasAmountOrPercentage` as '_hasAmountOrPercentage' satisfies AccessParameterLimitedFormPaths,
    ],
  });

  // when toggling off the specific % or $ checkbox, clear the list of amountsOrPercentages
  // so they don't get written to the BE
  const previousHasAmountOrPercentage = usePrevious(hasAmountOrPercentage);
  useEffect(() => {
    if (isUndefined(previousHasAmountOrPercentage)) return;
    if (previousHasAmountOrPercentage === hasAmountOrPercentage) return;

    if (previousHasAmountOrPercentage && !hasAmountOrPercentage) {
      setValue(
        `${fieldName}.amountsOrPercentages` as 'amountsOrPercentages' satisfies AccessParameterLimitedFormPaths,
        []
      );
    }
  }, [
    fieldName,
    hasAmountOrPercentage,
    previousHasAmountOrPercentage,
    setValue,
  ]);

  return (
    <Stack spacing={2}>
      <FormAwareCheckbox
        control={control}
        fieldName={
          `${fieldName}._hasAmountOrPercentage` as '_hasAmountOrPercentage' satisfies AccessParameterLimitedFormPaths
        }
        label="Specific $ or %"
      />
      {hasAmountOrPercentage && (
        <SubformContainer>
          <FormAwareInputRepeater<BeneficiaryFormAccessParameters>
            name={`${fieldName}.amountsOrPercentages`}
            control={control}
            allowEmpty={false}
            shouldInit
            emptyValue={{
              kind: AccessParameterKind.Percentage,
              _hasFrequency: false,
              frequency: EntityBeneficiaryAccessFrequencies.Ongoing,
              amount: undefined,
              percentage: undefined,
              _hasAgeParams: false,
              distributionType: DistributionType.Discretionary,
              ageParams: [
                {
                  _ageRequirementKind: 'upon' as const,
                },
              ],
            }}
            render={(idx) => {
              return (
                <>
                  <PartialAccessParameterForm
                    fieldName={
                      `${fieldName}.amountsOrPercentages.${idx}` as `amountsOrPercentages.${number}` satisfies AccessParameterLimitedFormPaths
                    }
                    includeOngoingFrequencyOption
                    variant={
                      PartialAccessParameterFormVariant.SpecificAmountOrPercentage
                    }
                    beneficiaryId={beneficiaryId}
                  />
                </>
              );
            }}
            addAnotherItemText="Add additional parameter"
          />
        </SubformContainer>
      )}
      <FormAwareCheckbox
        control={control}
        label="Under full discretion of trustee"
        fieldName={`${fieldName}.checkboxes.${AccessParameterKind.FullDiscretion}`}
      />
      {checkboxes[AccessParameterKind.FullDiscretion] && (
        <SubformContainer>
          <PartialAccessParameterForm
            fieldName={
              `${fieldName}.values.${AccessParameterKind.FullDiscretion}` as `values.${AccessParameterKind.FullDiscretion}` satisfies AccessParameterLimitedFormPaths
            }
            beneficiaryId={beneficiaryId}
          />
        </SubformContainer>
      )}
      <FormAwareCheckbox
        control={control}
        label="HMS"
        fieldName={`${fieldName}.checkboxes.${AccessParameterKind.Hms}`}
      />
      {checkboxes[AccessParameterKind.Hms] && (
        <SubformContainer>
          <PartialAccessParameterForm
            fieldName={
              `${fieldName}.values.${AccessParameterKind.Hms}` as `values.${AccessParameterKind.Hms}` satisfies AccessParameterLimitedFormPaths
            }
            beneficiaryId={beneficiaryId}
          />
        </SubformContainer>
      )}
      <FormAwareCheckbox
        control={control}
        label="HEMS"
        fieldName={`${fieldName}.checkboxes.${AccessParameterKind.Hems}`}
      />
      {checkboxes[AccessParameterKind.Hems] && (
        <SubformContainer>
          <PartialAccessParameterForm
            fieldName={
              `${fieldName}.values.${AccessParameterKind.Hems}` as `values.${AccessParameterKind.Hems}` satisfies AccessParameterLimitedFormPaths
            }
            beneficiaryId={beneficiaryId}
          />
        </SubformContainer>
      )}
      <FormAwareCheckbox
        control={control}
        label="Trust income"
        fieldName={`${fieldName}.checkboxes.${AccessParameterKind.AllTrustIncome}`}
      />
      {checkboxes[AccessParameterKind.AllTrustIncome] && (
        <SubformContainer>
          <PartialAccessParameterForm
            fieldName={
              `${fieldName}.values.${AccessParameterKind.AllTrustIncome}` as `values.${AccessParameterKind.AllTrustIncome}` satisfies AccessParameterLimitedFormPaths
            }
            variant={PartialAccessParameterFormVariant.TrustIncome}
            beneficiaryId={beneficiaryId}
          />
        </SubformContainer>
      )}
      <FormAwareCheckbox
        control={control}
        label="Other"
        fieldName={`${fieldName}.checkboxes.${AccessParameterKind.Other}`}
      />
      {checkboxes[AccessParameterKind.Other] && (
        <SubformContainer>
          <PartialAccessParameterForm
            fieldName={
              `${fieldName}.values.${AccessParameterKind.Other}` as `values.${AccessParameterKind.Other}` satisfies AccessParameterLimitedFormPaths
            }
            beneficiaryId={beneficiaryId}
          />
        </SubformContainer>
      )}
    </Stack>
  );
}
